import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FC } from 'react';
import { useBreakpointValue } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import 'swiper/css';
import 'swiper/css/pagination';
import { Pagination as SliderPagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { SwiperClass } from 'swiper/react';
import { isServer } from '../../../utils/utils';
import { GetIcon } from '../../icons';
import { Asset } from '../asset';
import { useBulletsSSR } from '../useBulletsSSR';
import { usePhotoGridStoreMutation } from '../usePhotoStore';
import { AssetInfoPropTypes, AssetType } from '../utils';
import styles from './CarouselView.module.scss';
import type { AssetInfo } from '../utils';

interface CarouselViewProps {
    assets: AssetInfo[];
}

export const CarouselView: FC<CarouselViewProps> = ({ assets }) => {
    const mutation = usePhotoGridStoreMutation();
    // TODO: Remove the eslint-disable-next-line when migrating to TypeScript 5 and Swiper typings
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const [swiperRef, setSwiperRef] = useState<SwiperClass>(null);
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const previewSlideWidth = useBreakpointValue({ base: 16, sm: 64 });

    const handleOnSlideChange = useCallback(
        (swiper: SwiperClass) => {
            // TODO: Remove the eslint-disable-next-line when migrating to TypeScript 5 and Swiper typings
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (currentSlideIndex !== swiper.activeIndex) {
                // TODO: Remove the eslint-disable-next-line when migrating to TypeScript 5 and Swiper typings
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
                setCurrentSlideIndex(swiper.activeIndex);
            }
        },
        [currentSlideIndex],
    );

    const slidesPerView = useMemo(
        // Calculate the size of the main slide in order to let the next slide to be partially visible
        // Take into account the .5rem (8px) btw each image
        () => (!isServer ? 1 + ((previewSlideWidth ?? 0) + 8) / window.innerWidth : 1),
        [previewSlideWidth],
    );

    const { data: bullets } = useBulletsSSR();

    useEffect(() => {
        // TODO: Remove the eslint-disable-next-line when migrating to TypeScript 5 and Swiper typings
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        swiperRef?.slideToLoop(0, 300, true);
    }, [swiperRef, assets]);

    return (
        <div
            data-testid="photo-grid-carousel-view"
            className={`w-screen ${styles.photoGridCarouselView}`}
        >
            <Swiper
                onSwiper={setSwiperRef}
                onSlideChange={handleOnSlideChange}
                spaceBetween={8}
                slidesPerView={slidesPerView}
                loop
                modules={[SliderPagination]}
                pagination={{
                    dynamicBullets: true,
                    dynamicMainBullets: 3,
                    clickable: true,
                    ...(bullets
                        ? {
                              renderBullet(index: number, className: string): string {
                                  const assetType =
                                      assets[index].assetType === AssetType.IMAGE
                                          ? 'IMAGE'
                                          : 'VIDEO';
                                  const colorMode = currentSlideIndex === index ? 'DARK' : 'LIGHT';

                                  const stringComponent = bullets[`${assetType}_${colorMode}`];

                                  if (stringComponent) {
                                      return stringComponent
                                          ? stringComponent.replace(/\{replace\}/, className)
                                          : '';
                                  }

                                  return '';
                              },
                          }
                        : {}),
                }}
            >
                {assets.map((asset, idx) => (
                    <SwiperSlide key={asset.id}>
                        <button
                            className="relative block w-full"
                            type="button"
                            onClick={() =>
                                mutation.mutate({
                                    selectedAsset: asset,
                                    isZoomModalOpen: true,
                                })
                            }
                        >
                            <Asset
                                asset={asset}
                                shouldLazyLoad={idx > 1}
                                hasHighFetchPriority={idx === 0}
                            />
                            <div className="absolute bottom-4 right-4 leading-[0]">
                                <GetIcon name="expand-arrow-circle" size="sm" />
                            </div>
                        </button>
                    </SwiperSlide>
                ))}
            </Swiper>
        </div>
    );
};

CarouselView.propTypes = {
    assets: PropTypes.arrayOf(AssetInfoPropTypes.isRequired).isRequired,
};
